import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface LoaderConfig {
  enabled: boolean;
  delay?: number; // Delay before showing loader (to avoid flashing for quick requests)
  minDisplayTime?: number; // Minimum time to show loader
}

@Injectable({
  providedIn: 'root'
})
export class LoaderService {
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private requestCount = 0;
  private loadingTimeout: any;
  private minDisplayTimeout: any;
  private loadingStartTime: number = 0;

  public loading$ = this.loadingSubject.asObservable();

  private config: LoaderConfig = {
    enabled: true,
    delay: 300, // Show loader after 300ms to avoid flashing
    minDisplayTime: 500 // Show loader for at least 500ms
  };

  /**
   * Configure the loader behavior
   */
  configure(config: Partial<LoaderConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Get current loader configuration
   */
  getConfig(): LoaderConfig {
    return { ...this.config };
  }

  /**
   * Enable or disable the loader
   */
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
    if (!enabled) {
      this.hide();
    }
  }

  /**
   * Show the loader (called by interceptor when request starts)
   */
  show(): void {
    if (!this.config.enabled) {
      return;
    }

    this.requestCount++;

    if (this.requestCount === 1) {
      // Clear any existing timeout
      this.clearTimeouts();

      // Show loader after delay to avoid flashing for quick requests
      this.loadingTimeout = setTimeout(() => {
        this.loadingStartTime = Date.now();
        this.loadingSubject.next(true);
      }, this.config.delay);
    }
  }

  /**
   * Hide the loader (called by interceptor when request completes)
   */
  hide(): void {
    if (this.requestCount > 0) {
      this.requestCount--;
    }

    if (this.requestCount === 0) {
      const hideLoader = () => {
        this.loadingSubject.next(false);
        this.clearTimeouts();
      };

      // If loader is currently showing, respect minimum display time
      if (this.loadingSubject.value && this.config.minDisplayTime) {
        const elapsedTime = Date.now() - this.loadingStartTime;
        const remainingTime = this.config.minDisplayTime - elapsedTime;

        if (remainingTime > 0) {
          this.minDisplayTimeout = setTimeout(hideLoader, remainingTime);
        } else {
          hideLoader();
        }
      } else {
        // Clear the pending show timeout if loader hasn't shown yet
        this.clearTimeouts();
        this.loadingSubject.next(false);
      }
    }
  }

  /**
   * Force hide the loader (useful for error handling)
   */
  forceHide(): void {
    this.requestCount = 0;
    this.clearTimeouts();
    this.loadingSubject.next(false);
  }

  /**
   * Get current loading state
   */
  isLoading(): boolean {
    return this.loadingSubject.value;
  }

  private clearTimeouts(): void {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }
    if (this.minDisplayTimeout) {
      clearTimeout(this.minDisplayTimeout);
      this.minDisplayTimeout = null;
    }
  }
} 